import * as types from 'constants/ActionTypes';
import { updateValidatorFieldsKeys } from './FinalizeOrder.utils';

const paymentInfoErrors = {
  firstName: '',
  lastName: '',
  cardNumber: '',
  cardType: '',
  expirationDateMonth: '',
  expirationDateYear: '',
};

const claimInfoFields = {
  claimNumber: '',
  insuredName: '',
  externalReferenceId: '', // Job number
  claimInfo: '',
  dateOfLoss: '',
  PONumber: '',
  catId: '',
  typeOfLoss: '',
  deductible: '',
  renewalDate: '',
  policyNumber: '',
  appointmentDate: '',
};

const initState = {
  upgradeFromReportId: '',
  upgradeESX: false,
  canAddEsxToCurrentProduct: false,
  upgradeProductList: [],
  productId: null,
  userId: '',
  upgradeUserEmail: '',
  error: '',
  paymentInfo: {
    firstName: '',
    lastName: '',
    cardNumber: '',
    cardType: '',
    expirationDateMonth: '',
    expirationDateYear: '',
    consent: false,
  },
  claimInfo: claimInfoFields,
  paymentInfoErrors,
  claimInfoErrors: claimInfoFields,
  estimatedCostResponse: null,
  fetchingEstimatedCost: false,
  loadingPage: false,
  loadFinalizeOrderError: false,
  placeOrderLoading: false,
  placeOrderError: false,
  isExpanded: {
    emailRecipients: false,
    paymentInformation: false,
    claimInformation: false,
    substitutionsAndCustomization: false,
  },
  upgradedProduct: null,
  upgradedReportId: null,
  upgradedReportAddress: [],
  upgradedReportDate: '',
  upgradedStructureType: '',
  upgradedReportStatus: '',
  upgradedReportNotFound: false,
  upgradedReportError: false,
  includeCustomCover: false,
  orderPreferences: {},
  substitutionsAndCustomizationValues: {},
  slaPropertyDataResult: null,
};

export default (state = initState, action = {}) => {
  switch (action.type) {
    case types.UPDATE_PAYMENT_INFO: return { ...state, paymentInfo: action.paymentInfo };
    case types.GET_ESTIMATED_COST_SUCCESS: return { ...state, estimatedCostResponse: action.estimatedCostResponse };
    case types.FETCHING_ESTIMATED_COST: return { ...state, fetchingEstimatedCost: true };
    case types.FETCHED_ESTIMATED_COST: return { ...state, fetchingEstimatedCost: false };
    case types.LOAD_UPGRADE_REPORT_FINALIZE_ORDER: return { ...state, upgradeESX: action.upgradeESX };
    case types.LOADING_FINALIZE_ORDER: return { ...state, loadingPage: true, loadFinalizeOrderError: false };
    case types.LOADED_FINALIZE_ORDER: {
      const orderPreferences = action.orderPreferences ?? {};
      const substitutionsAndCustomizationValues = action.substitutionsAndCustomizationValues ?? {};
      return {
        ...state,
        placeOrderError: false,
        loadingPage: false,
        loadFinalizeOrderError: false,
        upgradedReportError: false,
        includeCustomCover: Boolean(orderPreferences.reportOptionsCustomCover?.value),
        orderPreferences,
        substitutionsAndCustomizationValues,
      };
    }
    case types.LOAD_FINALIZE_ORDER_ERROR: return { ...state, loadingPage: false, loadFinalizeOrderError: true };
    case types.PLACE_ORDER_SUCCESS: return initState;
    case types.PLACE_ORDER_ERROR: return { ...state, placeOrderError: true, placeOrderLoading: false };
    case types.PLACE_ORDER_LOADING: return { ...state, placeOrderError: false, placeOrderLoading: true };
    case types.PLACE_ORDER_STOP_LOADING: return { ...state, placeOrderLoading: false };
    case types.CYBERSOURCE_TOKEN_ERROR: return {
      ...state,
      paymentInfoErrors: { ...state.paymentInfoErrors, isError: Boolean(action.error), cardNumber: action.error },
      isExpanded: { ...state.isExpanded, paymentInformation: true },
      placeOrderLoading: false,
    };
    case types.UPDATE_PAYMENT_INFO_ERRORS: return {
      ...state,
      paymentInfoErrors: action.paymentInfoErrors,
      isExpanded: { ...state.isExpanded, paymentInformation: action.paymentInfoErrors.isError },
    };
    case types.CLEAR_PAYMENT_INFO_ERRORS: return { ...state, paymentInfoErrors };
    case types.CANCEL_ORDER_SELECTION: return {
      ...state,
      ...initState,
    };
    case types.UPDATE_CLAIM_INFO: return { ...state, claimInfo: action.claimInfo };
    case types.EXPAND_PANEL: return { ...state, isExpanded: { ...state.isExpanded, [action.panelName]: action.isExpanded } };
    case types.VALIDATOR_FIELDS_SUCCESS: return {
      ...state,
      validatorFields: updateValidatorFieldsKeys(action.validatorFields, Object.keys(claimInfoFields)),
    };
    case types.UPDATE_CLAIM_INFO_ERRORS: return { ...state, claimInfoErrors: action.claimInfoErrors };
    case types.RESET_FINALIZE_ORDER_PAGE: return {
      ...state,
      claimInfoErrors: claimInfoFields,
      claimInfo: claimInfoFields,
      // Clear upgrade context so a later normal checkout cannot reuse stale upgrade pricing.
      upgradeFromReportId: '',
      upgradeESX: false,
      canAddEsxToCurrentProduct: false,
      upgradeProductList: [],
      productId: null,
      userId: '',
      upgradeUserEmail: '',
      // Clear cached pricing so Order Summary cannot show a prior upgrade's estimated-cost response.
      estimatedCostResponse: null,
      fetchingEstimatedCost: false,
    };
    case types.CLEAR_UPGRADE_FINALIZE_CONTEXT: return {
      ...state,
      upgradeFromReportId: '',
      upgradeESX: false,
      canAddEsxToCurrentProduct: false,
      upgradeProductList: [],
      productId: null,
      userId: '',
      upgradeUserEmail: '',
    };
    case types.UPGRADE_REPORT_DETAILS_SUCCESS: return {
      ...state,
      upgradeFromReportId: action.upgradeDetails.reportId,
      // #genai: Upgrade API controls whether ESX must be force-enabled for current upgrade flow.
      canAddEsxToCurrentProduct: Boolean(action.upgradeDetails.canAddEsxToCurrentProduct),
      upgradeProductList: action.upgradeDetails.upgradeProductList,
      productId: action.upgradeDetails.productId,
      userId: action.upgradeDetails.userID,
      upgradeUserEmail: action.upgradeDetails.upgradeUserEmail,
      claimInfo: {
        ...claimInfoFields,
        ...Object.keys(claimInfoFields).reduce((acc, key) => {
          if (action.upgradeDetails[key]) acc[key] = action.upgradeDetails[key];
          return acc;
        }, {}),
      },
    };
    case types.UPGRADED_REPORT_DETAILS_SUCCESS: return {
      ...state,
      upgradedProduct: action.upgradedReportDetails.upgradedProduct,
      upgradedReportId: action.upgradedReportDetails.reportId,
      upgradedReportAddress: action.upgradedReportDetails.reportAddress,
      upgradedReportDate: action.upgradedReportDetails.upgradeReportDate,
      upgradedStructureType: action.upgradedReportDetails.structureType,
      upgradedReportStatus: action.upgradedReportDetails.upgradeReportStatus,
      placeOrderLoading: false,
    };
    case types.UPGRADED_REPORT_NOT_FOUND: return { ...state, upgradedReportNotFound: true, loadingPage: false };
    case types.UPGRADED_REPORT_DETAILS_FAILURE: return {
      ...state,
      upgradedReportError: true,
      loadingPage: false,
      placeOrderLoading: false,
    };
    case types.UPDATE_INCLUDE_CUSTOM_COVER: return {
      ...state,
      includeCustomCover: action.includeCustomCover,
    };
    case types.UPDATE_SUBSTITUTION_CUSTOMIZATION_VALUES: return {
      ...state,
      substitutionsAndCustomizationValues: {
        ...state.substitutionsAndCustomizationValues,
        [action.propertyName]: {
          ...state.orderPreferences[action.propertyName],
          ...state.substitutionsAndCustomizationValues[action.propertyName],
          [action.propertyKey]: action.propertyValue,
        },
      },
    };
    default: return state;
  }
};
